import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Users, MapPin, FileText, Clock, Check, X, Loader2, TrendingUp, 
  BarChart3, Shield, ShieldCheck, ChevronDown, Search, Heart, Eye, UserCheck, AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [analytics, setAnalytics] = useState(null);
  const [pendingActivities, setPendingActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState(null);
  const [userFilter, setUserFilter] = useState('all');

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [analyticsRes, pendingRes, usersRes, venuesRes] = await Promise.all([
        axios.get(`${API}/api/admin/analytics`, { withCredentials: true }),
        axios.get(`${API}/api/admin/activities?status=pending`, { withCredentials: true }),
        axios.get(`${API}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API}/api/admin/venues`, { withCredentials: true })
      ]);
      setAnalytics(analyticsRes.data);
      setPendingActivities(pendingRes.data);
      setUsers(usersRes.data);
      setVenues(venuesRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (activityId) => {
    setActionLoading(activityId);
    try {
      await axios.put(`${API}/api/admin/activities/${activityId}/approve`, {}, { withCredentials: true });
      setPendingActivities(prev => prev.filter(a => a.id !== activityId));
      setAnalytics(prev => prev ? { ...prev, pending_activities: prev.pending_activities - 1 } : prev);
    } catch (error) { console.error('Error approving:', error); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (activityId) => {
    setActionLoading(activityId);
    try {
      await axios.put(`${API}/api/admin/activities/${activityId}/reject`, {}, { withCredentials: true });
      setPendingActivities(prev => prev.filter(a => a.id !== activityId));
      setAnalytics(prev => prev ? { ...prev, pending_activities: prev.pending_activities - 1 } : prev);
    } catch (error) { console.error('Error rejecting:', error); }
    finally { setActionLoading(null); }
  };

  const handleVerifyVenue = async (venueId) => {
    setActionLoading(venueId);
    try {
      const res = await axios.put(`${API}/api/admin/venues/${venueId}/verify`, {}, { withCredentials: true });
      setVenues(prev => prev.map(v => v.id === venueId ? { ...v, is_verified: res.data.verified } : v));
    } catch (error) { console.error('Error verifying venue:', error); }
    finally { setActionLoading(null); }
  };

  const handleRoleChange = async (userId, newRole) => {
    setActionLoading(userId);
    try {
      await axios.put(`${API}/api/admin/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (error) { console.error('Error updating role:', error); }
    finally { setActionLoading(null); }
  };

  const filteredUsers = userFilter === 'all' ? users : users.filter(u => u.role === userFilter);

  if (!isAdmin) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'pending', label: `Moderation (${analytics?.pending_activities || 0})`, icon: Clock },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'venues', label: 'Venues', icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#2C302E]/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/profile')} className="w-10 h-10 rounded-full bg-[#F9F8F6] flex items-center justify-center" data-testid="admin-back-btn">
                <ArrowLeft className="w-5 h-5 text-[#2C302E]" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#2C302E] font-['Outfit']">Admin Dashboard</h1>
                <p className="text-sm text-[#666B67]">Manage InfoBel platform</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4 overflow-x-auto hide-scrollbar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-pill whitespace-nowrap flex items-center gap-2 ${activeTab === tab.id ? 'tab-pill-active' : 'tab-pill-inactive'}`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" /></div>
        ) : (
          <>
            {/* ============ OVERVIEW ============ */}
            {activeTab === 'overview' && analytics && (
              <div className="space-y-6">
                {/* Primary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Total Users', value: analytics.total_users, icon: Users, color: '#E07A5F', bg: 'bg-[#E07A5F]/10' },
                    { label: 'Activities', value: analytics.total_activities, icon: MapPin, color: '#81B29A', bg: 'bg-[#81B29A]/10' },
                    { label: 'Pending Review', value: analytics.pending_activities, icon: Clock, color: '#D97706', bg: 'bg-amber-100' },
                    { label: 'Total Reviews', value: analytics.total_reviews, icon: FileText, color: '#7C3AED', bg: 'bg-purple-100' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-white rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                          <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                        </div>
                        <span className="text-sm text-[#666B67]">{stat.label}</span>
                      </div>
                      <p className="text-3xl font-bold text-[#2C302E]" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>{stat.value}</p>
                    </div>
                  ))}
                </div>

                {/* Engagement Stats */}
                <div className="bg-white rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-[#2C302E] mb-4 font-['Outfit']">Engagement Metrics</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                      { label: 'Saves', value: analytics.total_saves, icon: Heart, color: '#E07A5F' },
                      { label: 'Been There', value: analytics.total_been_there, icon: Check, color: '#81B29A' },
                      { label: 'Follows', value: analytics.total_follows, icon: UserCheck, color: '#3B82F6' },
                      { label: 'Verified Venues', value: `${analytics.verified_venues}/${analytics.venues_count}`, icon: ShieldCheck, color: '#7C3AED' },
                    ].map((metric) => (
                      <div key={metric.label} className="text-center">
                        <metric.icon className="w-6 h-6 mx-auto mb-2" style={{ color: metric.color }} />
                        <p className="text-2xl font-bold text-[#2C302E]">{metric.value}</p>
                        <p className="text-xs text-[#666B67]">{metric.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* User Breakdown */}
                <div className="bg-white rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-[#2C302E] mb-4 font-['Outfit']">User Breakdown</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(analytics.user_breakdown || {}).map(([role, count]) => (
                      <div key={role} className="flex items-center gap-3 p-3 bg-[#F9F8F6] rounded-xl">
                        <div className={`w-3 h-3 rounded-full ${
                          role === 'admins' ? 'bg-purple-500' :
                          role === 'venues' ? 'bg-[#E07A5F]' :
                          role === 'parents' ? 'bg-[#81B29A]' : 'bg-amber-500'
                        }`} />
                        <div>
                          <p className="text-sm font-medium text-[#2C302E] capitalize">{role}</p>
                          <p className="text-xs text-[#666B67]">{count} users</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-[#2C302E] mb-4 font-['Outfit']">Quick Actions</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Button variant="outline" onClick={() => setActiveTab('pending')} className="rounded-xl py-6 flex flex-col gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <span>Review Pending ({analytics.pending_activities})</span>
                    </Button>
                    <Button variant="outline" onClick={() => setActiveTab('venues')} className="rounded-xl py-6 flex flex-col gap-2">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <span>Manage Venues</span>
                    </Button>
                    <Button variant="outline" onClick={() => setActiveTab('users')} className="rounded-xl py-6 flex flex-col gap-2">
                      <Users className="w-5 h-5 text-[#81B29A]" />
                      <span>Manage Users</span>
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/discover')} className="rounded-xl py-6 flex flex-col gap-2">
                      <Eye className="w-5 h-5 text-[#E07A5F]" />
                      <span>View Feed</span>
                    </Button>
                  </div>
                </div>

                {/* Recent Reviews */}
                {analytics.recent_reviews?.length > 0 && (
                  <div className="bg-white rounded-2xl p-6">
                    <h2 className="text-lg font-semibold text-[#2C302E] mb-4 font-['Outfit']">Recent Reviews</h2>
                    <div className="space-y-3">
                      {analytics.recent_reviews.map((review, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-[#F9F8F6] rounded-xl">
                          <div className="w-8 h-8 rounded-full bg-[#81B29A]/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm text-[#81B29A]">{review.user_name?.charAt(0)}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2C302E]">{review.user_name}</p>
                            <p className="text-xs text-[#666B67] line-clamp-1">{review.comment}</p>
                          </div>
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, j) => (
                              <div key={j} className={`w-2 h-2 rounded-full ${j < review.rating ? 'bg-amber-400' : 'bg-gray-200'}`} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ============ MODERATION ============ */}
            {activeTab === 'pending' && (
              <div>
                <h2 className="text-lg font-semibold text-[#2C302E] mb-4 font-['Outfit']" data-testid="pending-heading">
                  Pending Activities ({pendingActivities.length})
                </h2>
                {pendingActivities.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <Check className="w-12 h-12 text-[#81B29A] mx-auto mb-4" />
                    <p className="text-[#2C302E] font-medium mb-1">All caught up!</p>
                    <p className="text-sm text-[#666B67]">No pending activities to review</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingActivities.map((activity) => (
                      <div key={activity.id} className="bg-white rounded-2xl p-5" data-testid={`pending-${activity.id}`}>
                        <div className="flex gap-4">
                          <img src={activity.images?.[0] || 'https://via.placeholder.com/120'} alt={activity.title} className="w-28 h-28 rounded-xl object-cover flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-[#2C302E] mb-1">{activity.title}</h3>
                            <p className="text-sm text-[#666B67] line-clamp-2 mb-2">{activity.summary}</p>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-[#666B67]">
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{activity.city}</span>
                              <span className="badge badge-secondary">{activity.category}</span>
                              <span className="badge badge-secondary">{activity.price_range}</span>
                              {activity.indoor_outdoor && <span className="badge badge-secondary">{activity.indoor_outdoor}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4 justify-end">
                          <Button size="sm" variant="outline" onClick={() => handleReject(activity.id)} disabled={actionLoading === activity.id} className="text-red-500 hover:bg-red-50" data-testid={`reject-${activity.id}`}>
                            {actionLoading === activity.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4 mr-1" />Reject</>}
                          </Button>
                          <Button size="sm" onClick={() => handleApprove(activity.id)} disabled={actionLoading === activity.id} className="bg-[#81B29A] hover:bg-[#6B9B83]" data-testid={`approve-${activity.id}`}>
                            {actionLoading === activity.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Approve</>}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ============ USERS ============ */}
            {activeTab === 'users' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-[#2C302E] font-['Outfit']">All Users ({filteredUsers.length})</h2>
                  <div className="flex gap-2">
                    {['all', 'parent', 'grandparent', 'venue', 'admin'].map(role => (
                      <button
                        key={role}
                        onClick={() => setUserFilter(role)}
                        className={`tab-pill text-xs capitalize ${userFilter === role ? 'tab-pill-active' : 'tab-pill-inactive'}`}
                        data-testid={`user-filter-${role}`}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="users-table">
                      <thead className="bg-[#F9F8F6]">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-[#666B67]">User</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-[#666B67]">Email</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-[#666B67]">Role</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-[#666B67]">Joined</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-[#666B67]">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((u) => (
                          <tr key={u.id} className="border-t border-[#2C302E]/5">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-[#81B29A]/20 flex items-center justify-center">
                                  <span className="text-sm text-[#81B29A]">{u.name?.charAt(0)?.toUpperCase()}</span>
                                </div>
                                <span className="font-medium text-[#2C302E]">{u.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#666B67]">{u.email}</td>
                            <td className="px-4 py-3">
                              <span className={`badge ${
                                u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                                u.role === 'venue' ? 'bg-[#E07A5F]/10 text-[#E07A5F]' :
                                'badge-secondary'
                              }`}>
                                {u.role}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#666B67]">{new Date(u.created_at).toLocaleDateString()}</td>
                            <td className="px-4 py-3">
                              {u.id !== user?.id && (
                                <select
                                  value={u.role}
                                  onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                  disabled={actionLoading === u.id}
                                  className="text-sm border border-[#2C302E]/10 rounded-lg px-2 py-1 bg-white"
                                  data-testid={`role-select-${u.id}`}
                                >
                                  <option value="parent">Parent</option>
                                  <option value="grandparent">Grandparent</option>
                                  <option value="venue">Venue</option>
                                  <option value="admin">Admin</option>
                                </select>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============ VENUES ============ */}
            {activeTab === 'venues' && (
              <div>
                <h2 className="text-lg font-semibold text-[#2C302E] mb-4 font-['Outfit']" data-testid="venues-heading">
                  Venue Management ({venues.length})
                </h2>
                {venues.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <Shield className="w-12 h-12 text-[#666B67] mx-auto mb-4" />
                    <p className="text-[#666B67]">No registered venues yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {venues.map((venue) => (
                      <div key={venue.id} className="bg-white rounded-2xl p-5 flex items-center gap-4" data-testid={`venue-${venue.id}`}>
                        <div className="w-12 h-12 rounded-xl bg-[#E07A5F]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-lg text-[#E07A5F] font-semibold">{venue.name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-[#2C302E]">{venue.name}</h3>
                            {venue.is_verified && <ShieldCheck className="w-4 h-4 text-[#81B29A]" />}
                          </div>
                          <p className="text-sm text-[#666B67]">{venue.email}</p>
                          <p className="text-xs text-[#666B67]">Joined {new Date(venue.created_at).toLocaleDateString()}</p>
                        </div>
                        <Button
                          size="sm"
                          variant={venue.is_verified ? "outline" : "default"}
                          onClick={() => handleVerifyVenue(venue.id)}
                          disabled={actionLoading === venue.id}
                          className={venue.is_verified ? "text-red-500 hover:bg-red-50" : "bg-[#81B29A] hover:bg-[#6B9B83]"}
                          data-testid={`verify-${venue.id}`}
                        >
                          {actionLoading === venue.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : venue.is_verified ? (
                            <><X className="w-4 h-4 mr-1" />Revoke</>
                          ) : (
                            <><ShieldCheck className="w-4 h-4 mr-1" />Verify</>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
